import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Skeleton, cn } from '../ui';
import type { AnyGraphNode } from '../types/graphNodes';
import { findEdgeNode, getEdge, nodeName } from '../types/graphNodes';
import { deityIconUrl } from '../bhajan/deityIcon';
import { deityAsciiName, isPlaceholderDeity } from '../bhajan/deityIdentity';

export interface BhajanCardData {
  nodeId: number;
  title: string;
  ragam?: string;
  talam?: string;
  deity?: string;
  composer?: string;
  ragamId?: number;
  talamId?: number;
  deityId?: number;
  composerId?: number;
}

export interface BhajanCardProps {
  data: BhajanCardData;
  language: string;
  variant?: 'default' | 'featured' | 'list-row';
  isLoading?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Deity icon — uses the same PNG assets as jnana-native bm. */
const DeityIcon: React.FC<{ deityId?: number; deityName?: string; size?: number }> = ({
  deityId,
  deityName,
  size = 28,
}) => (
  <img
    src={deityIconUrl(deityId)}
    alt={deityName ?? ''}
    aria-hidden={!deityName}
    width={size}
    height={size}
    className="rounded-full bg-bg-3/60 object-cover shrink-0"
    loading="lazy"
    style={{ width: size, height: size }}
  />
);

export const BhajanCard: React.FC<BhajanCardProps> = ({
  data,
  language,
  variant = 'default',
  isLoading,
  onClick,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={cn('w-full p-4', className)}>
        <div className="flex items-start justify-between mb-3">
          <Skeleton width={50} height={14} />
          <Skeleton circle width={20} height={20} />
        </div>
        <Skeleton width="80%" height={26} />
        <Skeleton width="60%" height={26} className="mt-1" />
        <div className="mt-4 space-y-2">
          <Skeleton width="70%" height={14} />
          <Skeleton width="85%" height={14} />
        </div>
      </Card>
    );
  }

  // Canonical URL pattern matches bhajanmala.com share links: /nodes/:id
  // Canonical URL pattern matches bhajanmala.com share links: /nodes/:id
  const href = `/nodes/${data.nodeId}`;

  if (variant === 'list-row') {
    const inner = (
      <div className="flex items-center gap-4 px-4 py-3 hover:bg-bg-3/60 transition-colors duration-150 group">
        <DeityIcon deityId={data.deityId} deityName={data.deity} size={28} />
        <span className="font-mono text-small text-text-tertiary tabular-nums w-12 shrink-0">
          #{data.nodeId}
        </span>
        <h3 className="font-display text-h3 text-text-primary truncate flex-1 group-hover:text-accent-gold transition-colors capitalize">
          {data.title}
        </h3>
        <div className="hidden sm:flex items-center gap-3 shrink-0 text-small text-text-secondary capitalize">
          {data.ragam && <span>{data.ragam}</span>}
          {data.talam && <span className="text-text-tertiary">·</span>}
          {data.talam && <span>{data.talam}</span>}
          {data.deity && <span className="text-text-tertiary">·</span>}
          {data.deity && <span>{data.deity}</span>}
        </div>
      </div>
    );
    return onClick ? (
      <button type="button" onClick={onClick} className={cn('block w-full text-left', className)}>{inner}</button>
    ) : (
      <Link to={href} className={cn('block', className)}>{inner}</Link>
    );
  }

  const isFeatured = variant === 'featured';

  const inner = (
    <Card
      interactive
      className={cn(
        'relative w-full h-full p-5 transition-all duration-250 ease-out-quint',
        'hover:-translate-y-0.5 hover:shadow-3 hover:ring-1 hover:ring-accent-gold/40',
        isFeatured && 'p-6'
      )}
    >
      {/* faint cosmic accent line on the left */}
      <span
        className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
        style={{ background: 'linear-gradient(180deg, var(--accent-gold) 0%, var(--accent-indigo) 100%)' }}
        aria-hidden
      />

      <div className="flex items-start justify-between mb-2">
        <span className="font-mono text-small text-text-tertiary tabular-nums">#{data.nodeId}</span>
        <DeityIcon deityId={data.deityId} deityName={data.deity} size={isFeatured ? 40 : 32} />
      </div>

      <h3
        className={cn(
          'font-display font-semibold text-text-primary leading-tight capitalize',
          isFeatured ? 'text-h2 mb-3' : 'text-h3 mb-2',
          'line-clamp-2'
        )}
      >
        {data.title}
      </h3>

      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-small">
        {data.ragam && (
          <>
            <dt className="text-text-tertiary italic">Ragam</dt>
            <dd className="text-text-secondary truncate capitalize">{data.ragam}</dd>
          </>
        )}
        {data.talam && (
          <>
            <dt className="text-text-tertiary italic">Talam</dt>
            <dd className="text-text-secondary truncate capitalize">{data.talam}</dd>
          </>
        )}
        {data.deity && (
          <>
            <dt className="text-text-tertiary italic">Deity</dt>
            <dd className="text-text-secondary truncate capitalize">{data.deity}</dd>
          </>
        )}
        {data.composer && (
          <>
            <dt className="text-text-tertiary italic">Composer</dt>
            <dd className="text-text-secondary truncate capitalize">{data.composer}</dd>
          </>
        )}
      </dl>
    </Card>
  );

  return onClick ? (
    <button type="button" onClick={onClick} className={cn('group block w-full h-full text-left focus-visible:outline-none', className)}>{inner}</button>
  ) : (
    <Link to={href} className={cn('group block h-full focus-visible:outline-none', className)}>{inner}</Link>
  );
};

/* Convert any graph node + its edge targets into BhajanCardData. */
export function bhajanToCardData(node: AnyGraphNode, allNodes: AnyGraphNode[]): BhajanCardData {
  const ragamE = getEdge(node, 'ragam');
  const talamE = getEdge(node, 'talam');
  const deityE = getEdge(node, 'deity');
  const composerE = getEdge(node, 'composer');
  // "stotram" / "mantram" as a deity is a placeholder — a bhajan-type
  // sub-class, not a real deity — so the value is dropped entirely. Decided
  // from the node id and the ASCII name, never the displayed one, which is
  // native script in every language but English.
  const deityNode = findEdgeNode(node, 'deity', allNodes);
  const deityName = isPlaceholderDeity(deityNode?.nodeId, deityAsciiName(deityNode))
    ? undefined
    : nodeName(deityNode);

  return {
    nodeId: node.nodeId,
    title: nodeName(node) ?? `Bhajan ${node.nodeId}`,
    ragam: nodeName(findEdgeNode(node, 'ragam', allNodes)),
    talam: nodeName(findEdgeNode(node, 'talam', allNodes)),
    deity: deityName,
    composer: nodeName(findEdgeNode(node, 'composer', allNodes)),
    ragamId: ragamE?.nodeId,
    talamId: talamE?.nodeId,
    // Drop deityId too so we don't show a deity icon for placeholder deities.
    deityId: deityName ? deityE?.nodeId : undefined,
    composerId: composerE?.nodeId,
  };
}
